// src/pages/AddAccident.jsx
import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import normalize, { fuzzyIncludes } from "../utils/normalize";

const AddAccident = () => {
  const [mode, setMode] = useState("substation"); // 'substation' | 'network'
  const [substations, setSubstations] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedObject, setSelectedObject] = useState(null);

  const [formValues, setFormValues] = useState({
    date: "",
    repair_date: "",
    topic: "",
    protection_type: "",
    recorder_reading: "",
    cause: "",
    measures: "",
    result: "",
    employee_details: "",
  });

  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [apiError, setApiError] = useState("");

  // --- сотрудники ---
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  // ---- helpers ----

  const isSubstationMode = mode === "substation";

  const token = useMemo(() => localStorage.getItem("token"), []);

  const authHeader = useMemo(
    () =>
      token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
    [token]
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const [subRes, netRes] = await Promise.all([
          apiFetch("/api/substation", { cache: "no-store" }),
          apiFetch("/api/network", { cache: "no-store" }),
        ]);

        const [subList, netList] = await Promise.all([subRes.json(), netRes.json()]);
        setSubstations(Array.isArray(subList) ? subList : []);
        setNetworks(Array.isArray(netList) ? netList : []);
      } catch (e) {
        console.error("Error loading substations/networks", e);
      }
    };

    loadData();
  }, []);

  const getDisplayName = (item) =>
    (item && (item.name || item.title || item.slug)) || "—";

  const getBranchName = (item) => {
    if (!item) return "";

    // 1) самое главное — город, который мы теперь включаем на бэке
    if (item.city && item.city.name) {
      return item.city.name;
    }

    // 2) старые варианты — на всякий случай как fallback
    const info = item.info || {};

    return (
      info.filial ||
      info.filial_name ||
      info.branch ||
      info.city ||
      info.region ||
      ""
    );
  };

  const filteredList = useMemo(() => {
    const list = isSubstationMode ? substations : networks;
    if (!search.trim()) return list.slice(0, 25);

    return list
      .filter((item) => {
        const name = normalize(getDisplayName(item));
        const branch = normalize(getBranchName(item));
        return fuzzyIncludes(name, search) || fuzzyIncludes(branch, search);
      })
      .slice(0, 25);
  }, [isSubstationMode, substations, networks, search]);

  const handleSelectObject = (item) => {
    setSelectedObject({
      type: isSubstationMode ? "substation" : "network",
      id: item.id,
      name: getDisplayName(item),
      branch: getBranchName(item),
    });

    // Сброс успеха при новом выборе
    setSuccessMessage("");
    setApiError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const handleFilesChange = (e) => {
    const fileList = Array.from(e.target.files || []);
    setFiles(fileList);
  };

  // --- поиск сотрудников (автокомплит) ---
  useEffect(() => {
    const q = employeeSearch.trim();

    if (!q || q.length < 2) {
      setEmployeeOptions([]);
      setEmployeeLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        setEmployeeLoading(true);
        const res = await apiFetch(`/api/employees?search=${encodeURIComponent(q)}`, {
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error("Failed to load employees");
        }

        const list = await res.json();
        setEmployeeOptions(Array.isArray(list) ? list : []);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading employees:", err);
        }
      } finally {
        setEmployeeLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [employeeSearch, authHeader]);

  const handleAddEmployee = (emp) => {
    setSelectedEmployees((prev) => {
      if (prev.some((e) => e.id === emp.id)) return prev;
      return [...prev, emp];
    });
    setEmployeeSearch("");
    setEmployeeOptions([]);
  };

  const handleRemoveEmployee = (id) => {
    setSelectedEmployees((prev) => prev.filter((e) => e.id !== id));
  };

  const handleClearEmployees = () => {
    setSelectedEmployees([]);
  };

  // Валидация по моделям
  // substation_accident: event_date, topic, protection_type, cause, measures
  // network_accident: date, topic, result, protection_type, recorder_reading, cause, measures
  const validate = () => {
    const newErrors = {};

    if (!selectedObject) {
      newErrors.object = "Подстанция ёки линия танланг.";
    }

    return newErrors;
  };

  const inputClass = (name) => {
    const hasError = Boolean(errors[name]);
    const hasValue = Boolean(formValues[name]);
    return (
      "accident-add__input" +
      (hasError ? " accident-add__input--error" : "") +
      (!hasError && hasValue ? " accident-add__input--ok" : "")
    );
  };

  const textareaClass = (name) => {
    const hasError = Boolean(errors[name]);
    const hasValue = Boolean(formValues[name]);
    return (
      "accident-add__textarea" +
      (hasError ? " accident-add__textarea--error" : "") +
      (!hasError && hasValue ? " accident-add__textarea--ok" : "")
    );
  };

  const hasAnyText = Object.values(formValues).some((v) => String(v || "").trim());

  const canSubmit =
    !!selectedObject &&
    hasAnyText &&
    !isSubmitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setApiError("");

    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    if (!selectedObject) return;

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      const appendIf = (key, value) => {
        if (value === undefined || value === null) return;
        // если файл — не трогаем trim
        if (value instanceof File) {
          formData.append(key, value);
          return;
        }
        const v = String(value).trim();
        if (v) formData.append(key, v);
      };

      if (selectedObject.type === "substation") {
        formData.append("sub_accident_id", selectedObject.id);
        appendIf("event_date", formValues.date);
        if (formValues.repair_date) {
          appendIf("repair_date", formValues.repair_date);
        }
        appendIf("topic", formValues.topic.trim());
        appendIf("protection_type", formValues.protection_type.trim());
        appendIf("cause", formValues.cause.trim());
        appendIf("measures", formValues.measures.trim());
      } else {
        formData.append("network_id_accident", selectedObject.id);
        appendIf("date", formValues.date);
        if (formValues.repair_date) {
          appendIf("repair_date", formValues.repair_date);
        }
        appendIf("topic", formValues.topic.trim());
        appendIf("result", formValues.result.trim());
        appendIf("protection_type", formValues.protection_type.trim());
        appendIf("recorder_reading", formValues.recorder_reading.trim());
        appendIf("cause", formValues.cause.trim());
        appendIf("measures", formValues.measures.trim());
      }

      // текст по сотрудникам (общий)
      if (formValues.employee_details.trim()) {
        appendIf(
          "employee_details",
          formValues.employee_details.trim()
        );
      }

      // список сотрудников (id)
      if (selectedEmployees.length > 0) {
        const ids = selectedEmployees.map((e) => e.id);
        appendIf("employee_ids", JSON.stringify(ids));
      }

      // Файлы — массив
      files.forEach((file) => {
        appendIf("files", file);
      });

      const url =
        selectedObject.type === "substation"
          ? "/api/substation-accidents"
          : "/api/network-accidents";

      const res = await apiFetch(url, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Error creating accident", text);
        setApiError("Хатолик юз берди. Кейинроқ уриниб кўринг.");
      } else {
        setSuccessMessage("Ўчиш муваффақиятли сақланди ✅");
        setFormValues({
          date: "",
          repair_date: "",
          topic: "",
          protection_type: "",
          recorder_reading: "",
          cause: "",
          measures: "",
          result: "",
          employee_details: "",
        });
        setFiles([]);
        // объект и выбранных сотрудников оставляем — удобно вносить несколько аварий подряд
      }
    } catch (err) {
      console.error(err);
      setApiError("Сервер билан боғланишда хатолик.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const modeLabel = isSubstationMode ? "Подстанция" : "Линия";
  const availableObjects = isSubstationMode ? substations.length : networks.length;
  const filledFieldsCount = Object.values(formValues).filter((v) => String(v || "").trim()).length;

  return (
    <Layout>
      <div className="container">
        <main className="accident-add">
          <section className="accident-add__hero">
            <div>
              <span className="accident-add__kicker">Оператив киритиш</span>
              <h1 className="accident-add__title">Ўчишни қўшиш</h1>
              <p>
                Объектни танланг, асосий техник маълумотларни киритинг ва фото
                материалларни бириктиринг.
              </p>
            </div>

            <div className="accident-add__status-grid">
              <div className="accident-add__status-card">
                <span>Режим</span>
                <strong>{modeLabel}</strong>
              </div>
              <div className="accident-add__status-card">
                <span>Объектлар</span>
                <strong>{availableObjects}</strong>
              </div>
              <div className="accident-add__status-card">
                <span>Тўлдирилган</span>
                <strong>{filledFieldsCount}</strong>
              </div>
              <div className="accident-add__status-card">
                <span>Медиа</span>
                <strong>{files.length}</strong>
              </div>
            </div>
          </section>

          <form className="accident-add__form" onSubmit={handleSubmit}>
            <section className="accident-add__section">
              <div className="accident-add__section-head">
                <div>
                  <span className="accident-add__section-number">01</span>
                  <h2>Объект</h2>
                </div>
                <p>Подстанция ёки линияни танланг.</p>
              </div>

              <div className="accident-add__type-switch">
                <button
                  type="button"
                  className={
                    "accident-add__type-btn" +
                    (isSubstationMode ? " accident-add__type-btn--active" : "")
                  }
                  onClick={() => {
                    setMode("substation");
                    setSelectedObject(null);
                    setSearch("");
                    setSuccessMessage("");
                    setApiError("");
                  }}
                >
                  Подстанциялар
                </button>
                <button
                  type="button"
                  className={
                    "accident-add__type-btn" +
                    (!isSubstationMode ? " accident-add__type-btn--active" : "")
                  }
                  onClick={() => {
                    setMode("network");
                    setSelectedObject(null);
                    setSearch("");
                    setSuccessMessage("");
                    setApiError("");
                  }}
                >
                  Линиялар
                </button>
              </div>

              <div className="accident-add__search-block">
                <input
                  type="text"
                  className={
                    "accident-add__search-input" +
                    (errors.object ? " accident-add__search-input--error" : "")
                  }
                  placeholder={
                    isSubstationMode
                      ? "Подстанция номини киритинг"
                      : "Линия номини киритинг"
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {errors.object && (
                  <div className="accident-add__error">{errors.object}</div>
                )}

                <div className="accident-add__search-list">
                  {filteredList.map((item) => {
                    const id = item.id;
                    const isActive =
                      selectedObject &&
                      selectedObject.type ===
                        (isSubstationMode ? "substation" : "network") &&
                      selectedObject.id === id;

                    return (
                      <button
                        key={id}
                        type="button"
                        className={
                          "accident-add__search-item" +
                          (isActive ? " accident-add__search-item--active" : "")
                        }
                        onClick={() => handleSelectObject(item)}
                      >
                        <div className="accident-add__search-item-name">
                          {getDisplayName(item)}
                        </div>
                        <div className="accident-add__search-item-meta">
                          {getBranchName(item) || "Филиал кўрсатилмаган"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedObject && (
                <div className="accident-add__object-info">
                  <div className="accident-add__object-title">
                    {selectedObject.type === "substation" ? "Подстанция" : "Линия"}:
                    <span>{selectedObject.name}</span>
                  </div>

                  {selectedObject.branch && (
                    <div className="accident-add__object-meta">
                      Филиал: <span>{selectedObject.branch}</span>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="accident-add__section">
              <div className="accident-add__section-head">
                <div>
                  <span className="accident-add__section-number">02</span>
                  <h2>Вақт ва ҳимоя</h2>
                </div>
                <p>Ўчиш вақти, тиклаш вақти ва ҳимоя маълумотлари.</p>
              </div>

              <div className="accident-add__form-grid">
                <div className="accident-add__field">
                  <label htmlFor="date">Ўчиш содир болган вақти</label>
                  <input
                    type="datetime-local"
                    name="date"
                    value={formValues.date}
                    onChange={handleChange}
                    className={inputClass("date")}
                  />
                  {errors.date && (
                    <div className="accident-add__error">{errors.date}</div>
                  )}
                </div>

                <div className="accident-add__field">
                  <label htmlFor="repair_date">Ишга киритилган вақти</label>
                  <input
                    type="datetime-local"
                    name="repair_date"
                    value={formValues.repair_date}
                    onChange={handleChange}
                    className={inputClass("repair_date")}
                  />
                </div>

                <div className="accident-add__field">
                  <label htmlFor="protection_type">Ишлаган ҳимоя тури</label>
                  <input
                    type="text"
                    name="protection_type"
                    placeholder="Ишлаган ҳимоя тури"
                    value={formValues.protection_type}
                    onChange={handleChange}
                    className={inputClass("protection_type")}
                  />
                  {errors.protection_type && (
                    <div className="accident-add__error">
                      {errors.protection_type}
                    </div>
                  )}
                </div>

                {!isSubstationMode && (
                  <div className="accident-add__field">
                    <label htmlFor="recorder_reading">Мослама кўрсаткичи</label>
                    <input
                      type="text"
                      name="recorder_reading"
                      placeholder="Қайд этувчи мослама кўрсаткичи"
                      value={formValues.recorder_reading}
                      onChange={handleChange}
                      className={inputClass("recorder_reading")}
                    />
                    {errors.recorder_reading && (
                      <div className="accident-add__error">
                        {errors.recorder_reading}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>

            <section className="accident-add__section">
              <div className="accident-add__section-head">
                <div>
                  <span className="accident-add__section-number">03</span>
                  <h2>Сабаб ва чоралар</h2>
                </div>
                <p>Тафсилотлар кейинги реестр ва update history учун сақланади.</p>
              </div>

              <div className="accident-add__form-grid">

              <div className="accident-add__field accident-add__field--full">
                <label htmlFor="topic">Ўчиш тури</label>
                <textarea
                  name="topic"
                  placeholder="Ўчиш тури"
                  value={formValues.topic}
                  onChange={handleChange}
                  className={textareaClass("topic")}
                  rows={3}
                />
                {errors.topic && (
                  <div className="accident-add__error">{errors.topic}</div>
                )}
              </div>

              <div className="accident-add__field accident-add__field--full">
                <label htmlFor="cause">Аниқланган сабаби</label>
                <textarea
                  name="cause"
                  placeholder="Аниқланган сабаби"
                  value={formValues.cause}
                  onChange={handleChange}
                  className={textareaClass("cause")}
                  rows={3}
                />
                {errors.cause && (
                  <div className="accident-add__error">{errors.cause}</div>
                )}
              </div>

              <div className="accident-add__field accident-add__field--full">
                <label htmlFor="measures">Кўрилган чоралар</label>
                <textarea
                  name="measures"
                  placeholder="Кўрилган чоралар"
                  value={formValues.measures}
                  onChange={handleChange}
                  className={textareaClass("measures")}
                  rows={3}
                />
                {errors.measures && (
                  <div className="accident-add__error">{errors.measures}</div>
                )}
              </div>

              {!isSubstationMode && (
                <div className="accident-add__field accident-add__field--full">
                  <label htmlFor="result">АҚУ натижаси</label>
                  <textarea
                    name="result"
                    placeholder="АҚУ натижаси"
                    value={formValues.result}
                    onChange={handleChange}
                    className={textareaClass("result")}
                    rows={2}
                  />
                  {errors.result && (
                    <div className="accident-add__error">
                      {errors.result}
                    </div>
                  )}
                </div>
              )}
              </div>
            </section>

            <section className="accident-add__section">
              <div className="accident-add__section-head">
                <div>
                  <span className="accident-add__section-number">04</span>
                  <h2>Медиа</h2>
                </div>
                <p>Фото ва файллар ихтиёрий равишда бириктирилади.</p>
              </div>

              <div className="accident-add__files">
                <div className="accident-add__files-label">
                  Медиа (ихтиёрий)
                </div>
                <input
                  type="file"
                  multiple
                  onChange={handleFilesChange}
                  className="accident-add__files-input"
                />
                {files.length > 0 && (
                  <ul className="accident-add__files-list">
                    {files.map((file) => (
                      <li key={file.name} className="accident-add__files-item">
                        {file.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {apiError && (
              <div className="accident-add__alert accident-add__alert--error">
                {apiError}
              </div>
            )}

            {successMessage && (
              <div className="accident-add__alert accident-add__alert--success">
                {successMessage}
              </div>
            )}

            <div className="accident-add__actions">
              <button
                type="submit"
                className="accident-add__submit"
                disabled={!canSubmit}
              >
                {isSubmitting ? "Сақланмоқда..." : "Ўчишни сақлаш"}
              </button>
            </div>
          </form>
        </main>
      </div>
    </Layout>
  );
};

export default AddAccident;
